/**
 * FoundryPerthPage — /foundry/perth spoke route.
 * DRAFT — Perth-specific face-to-face Foundry cohort.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import Button from '../components/ui/Button.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import {
  foundryPerthHead,
  foundryPerthIntro,
  foundryPerthWhy,
  foundryPerthFormat,
  foundryPerthCta,
  foundryPerthLinks,
} from '../content/foundryPerth.js';

const foundryPerthServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'The Foundry — Perth',
  description: 'Face-to-face AI founder cohort in Perth, Western Australia. Six weeks. Five founders per cohort. AUD pricing for Australian seats.',
  provider: { '@type': 'Organization', name: "The Artificer's AI" },
  areaServed: { '@type': 'City', name: 'Perth', containedInPlace: { '@type': 'State', name: 'Western Australia' } },
  serviceType: 'AI founder cohort (face-to-face)',
};

const foundryPerthLocalBusiness = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: "The Artificer's AI — The Foundry Perth",
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Perth',
    addressRegion: 'WA',
    postalCode: '6000',
    addressCountry: 'AU',
  },
  telephone: '+61-433-987-271',
  url: 'https://artificersai.com/foundry/perth',
};

const foundryPerthBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://artificersai.com/' },
    { '@type': 'ListItem', position: 2, name: 'The Foundry', item: 'https://artificersai.com/foundry' },
    { '@type': 'ListItem', position: 3, name: 'Perth', item: 'https://artificersai.com/foundry/perth' },
  ],
};

export default function FoundryPerthPage() {
  return (
    <>
      <Helmet>
        <title>The Foundry Perth — Face-to-Face AI Cohort | The Artificer's AI</title>
        <meta name="description" content="Face-to-face AI founder cohort in Perth, Western Australia. Build real systems in six weeks." />
        <link rel="canonical" href="https://artificersai.com/foundry/perth" />
        <meta property="og:title" content="The Foundry Perth — Face-to-Face AI Cohort" />
        <meta property="og:description" content="Face-to-face AI founder cohort in Perth, Western Australia. Build real systems in six weeks." />
        <meta property="og:url" content="https://artificersai.com/foundry/perth" />
      </Helmet>

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <Reveal as="div" delay={0} className="mb-4">
            <Eyebrow>{foundryPerthHead.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal as="h1" delay={80} className="text-display-lg font-display font-semibold mb-4">{foundryPerthHead.heading}</Reveal>
          <Reveal as="p" delay={160} className="text-body-lg text-ink-200 mb-8">{foundryPerthHead.subheading}</Reveal>
          <Reveal as="p" delay={220} className="text-body text-ink-200 mb-8">{foundryPerthIntro}</Reveal>
          <Reveal as="div" delay={280}>
            <Button as={Link} to="/intake">{foundryPerthCta.label}</Button>
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" delay={0} className="text-h2 font-display font-semibold mb-6">{foundryPerthWhy.heading}</Reveal>
          <Reveal as="div" delay={80}>
            {foundryPerthWhy.paragraphs.map((p, i) => (
              <p key={i} className="text-body text-ink-200 mb-4">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" delay={0} className="text-h2 font-display font-semibold mb-6">{foundryPerthFormat.heading}</Reveal>
          <ul className="flex flex-col gap-3">
            {foundryPerthFormat.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200 pl-4 border-l border-white/[.08]">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <div className="flex flex-col gap-4 mb-10">
            <Reveal as="div" delay={0}>
              <Link to={foundryPerthLinks.foundryGlobal.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
                {foundryPerthLinks.foundryGlobal.label}
              </Link>
            </Reveal>
            <Reveal as="div" delay={80}>
              <Link to={foundryPerthLinks.automatedSystems.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
                {foundryPerthLinks.automatedSystems.label}
              </Link>
            </Reveal>
          </div>
          <Reveal as="div" delay={160}>
            <Button as={Link} to="/intake">{foundryPerthCta.label}</Button>
          </Reveal>
        </div>
      </section>

      <SchemaJsonLd data={foundryPerthServiceSchema} />
      <SchemaJsonLd data={foundryPerthLocalBusiness} />
      <SchemaJsonLd data={foundryPerthBreadcrumb} />
    </>
  );
}
